import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Edit2, Trash2, X, Plus, ClipboardList, CheckCircle2, XCircle, LayoutList } from "lucide-react";
import { useReportTypes } from "../context/ReportTypesContext";
import Pagination, { usePagination } from "../shared/Pagination";

export default function ReportTypesTable() {
  const { reportTypes, loading, addReportType, updateReportType, deleteReportType, toggleReportStatus } = useReportTypes();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy,     setSortBy]     = useState("");
  const [modal,      setModal]      = useState({ type: null, item: null });

  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    let data = reportTypes.filter(r =>
      r.title.toLowerCase().includes(t) ||
      r.description.toLowerCase().includes(t) ||
      r.status.includes(t)
    );
    if (sortBy === "title-asc")   data = [...data].sort((a, b) => a.title.localeCompare(b.title));
    if (sortBy === "title-desc")  data = [...data].sort((a, b) => b.title.localeCompare(a.title));
    if (sortBy === "status-asc")  data = [...data].sort((a, b) => a.status.localeCompare(b.status));
    if (sortBy === "status-desc") data = [...data].sort((a, b) => b.status.localeCompare(a.status));
    return data;
  }, [reportTypes, searchTerm, sortBy]);

  const { page, pageSize, setPage, setPageSize, totalPages, totalItems, paginated } = usePagination(filtered, 10);

  const activeCount   = reportTypes.filter(r => r.status === "active").length;
  const inactiveCount = reportTypes.length - activeCount;

  const closeModal = () => setModal({ type: null, item: null });

  if (loading) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label:"Total Types", value:reportTypes.length, accent:"border-l-teal-500",  color:"text-teal-700",  bg:"bg-teal-50",  icon:LayoutList   },
            { label:"Active",      value:activeCount,         accent:"border-l-green-500", color:"text-green-600", bg:"bg-green-50", icon:CheckCircle2 },
            { label:"Inactive",    value:inactiveCount,       accent:"border-l-red-400",   color:"text-red-500",   bg:"bg-red-50",   icon:XCircle      },
          ].map(({ label, value, accent, color, bg, icon: Icon }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search + Sort + Add */}
        <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
          <input
            type="text"
            placeholder="Search issue types…"
            className="flex-1 min-w-[180px] px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <select
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 outline-none cursor-pointer focus:border-teal-400 min-w-[160px]"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="">Sort By</option>
            <option value="title-asc">Title A → Z</option>
            <option value="title-desc">Title Z → A</option>
            <option value="status-asc">Active First</option>
            <option value="status-desc">Inactive First</option>
          </select>
          <button
            onClick={() => setModal({ type: "add", item: null })}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0"
          >
            <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Issue Type</span>
          </button>
        </div>

        {/* Results count */}
        <p className="text-xs text-gray-400">
          Showing <span className="font-semibold text-gray-600">{filtered.length}</span> of{" "}
          <span className="font-semibold text-gray-600">{reportTypes.length}</span> issue types
        </p>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-teal-700 text-white">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Id</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Description</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-center">Status</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">
                    No issue types found.
                  </td>
                </tr>
              ) : paginated.map((item, idx) => (
                <tr key={item.id} className={`transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                  <td className="px-4 py-3 text-xs text-gray-400 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 font-semibold text-gray-800">{item.title}</td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell max-w-xs truncate">{item.description}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleReportStatus(item)}
                      className={`relative inline-flex items-center w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none
                        ${item.status === "active" ? "bg-green-500" : "bg-gray-300"}`}
                      title={item.status === "active" ? "Active – click to deactivate" : "Inactive – click to activate"}
                    >
                      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${item.status === "active" ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setModal({ type: "edit", item })}
                        className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setModal({ type: "delete", item })}
                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 border-t border-gray-100">
            <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
              pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </div>
        </div>
      </div>

      {modal.type === "add"    && <IssueTypeModal mode="add"  onClose={closeModal} onSave={data => { addReportType(data); closeModal(); }} />}
      {modal.type === "edit"   && <IssueTypeModal mode="edit" item={modal.item} onClose={closeModal} onSave={data => { updateReportType({ ...modal.item, ...data }); closeModal(); }} />}
      {modal.type === "delete" && <DeleteConfirm  item={modal.item} onClose={closeModal} onDelete={() => { deleteReportType(modal.item.id); closeModal(); }} />}
    </div>
  );
}

// ── Shared Add/Edit Modal ─────────────────────────────────────────────────────
function IssueTypeModal({ mode, item, onClose, onSave }) {
  const [form, setForm] = useState({
    title:       item?.title       || "",
    description: item?.description || "",
    status:      item?.status      || "active",
  });
  const isEdit = mode === "edit";

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="bg-teal-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-base font-semibold text-white">
              {isEdit ? "Edit Issue Type" : "Add Issue Type"}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Title <span className="text-red-400">*</span></label>
            <input
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Bug Report, Feature Request"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Description <span className="text-red-400">*</span></label>
            <textarea
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition resize-none"
              rows={4}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe this issue type…"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Status</label>
            <select
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium">
            Cancel
          </button>
          <button
            onClick={() => { if (!form.title.trim()) return; onSave(form); }}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm"
          >
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

// ── Delete Confirm ────────────────────────────────────────────────────────────
function DeleteConfirm({ item, onClose, onDelete }) {
  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden">
        <div className="h-1.5 bg-red-500 w-full" />
        <div className="p-6 text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-base font-bold text-gray-800 mb-1">Delete Issue Type?</h2>
          <p className="text-sm text-gray-500 mb-5">
            You're about to delete <strong className="text-gray-800">{item.title}</strong>. This cannot be undone.
          </p>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 font-medium transition-colors">
              Cancel
            </button>
            <button onClick={onDelete}
              className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold transition-colors shadow-sm">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
